
import Vue, { PropType } from 'vue';
import Api from '@/core/api';
import { Unsubscriber } from '@/core/event-bus';
import { cloneDeep, get, isFunction } from 'lodash';
import canAutoPlay from 'can-autoplay';
import { PlayerState } from '@/core/player-state';
import { durationToDays, durationToHMS, nowUnixTimestamp, endOfDay } from '@/common/time';

interface CountdownConfig {
    startTime: number,
    streamEnded: boolean,
    streamIsLive: boolean
}

export default Vue.extend({
    name: 'Countdown',

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        config: {
            type: Object as PropType<CountdownConfig>,
            default: () => ({} as CountdownConfig)
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        }
    },

    data() {
        return {
            unsubscribers: [] as Unsubscriber[],
            timeLeft: 0 as number,
            countdownInterval: 0,
            muted: false,
            STATES: {
                STREAM_IS_LIVE: 'streamIsLive',
                COUNTDOWN_ONGOING: 'countdownOngoing',
                COUNTDOWN_ENDED: 'countdownEnded',
                STREAM_ENDED: 'streamEnded'
            } as Record<string, string>
        };
    },

    computed: {
        startTime(): number {
            return this.config?.startTime ?? 0;
        },

        streamEnded(): boolean {
            return !!this.config?.streamEnded;
        },

        streamIsLive(): boolean {
            return !!this.config?.streamIsLive;
        },

        state(): string {
            if (this.streamEnded) {
                return this.STATES.STREAM_ENDED;
            }
            if (this.streamIsLive) {
                return this.STATES.STREAM_IS_LIVE;
            }
            if (this.timeLeft > 0) {
                return this.STATES.COUNTDOWN_ONGOING;
            } else {
                return this.STATES.COUNTDOWN_ENDED;
            }
        },

        countdownTime(): string {
            if (!this.timeLeft || this.timeLeft <= 0) {
                return '00:00:00';
            }

            const daysLeft = durationToDays(this.timeLeft);

            if (daysLeft >= 1) {
                // use end of day for the start time to be sure that if the start time is in 2 days
                // at 8am and this is checked at 8pm it actually shows 2 days, even though there
                // is only 1.5 days to go
                const adjustedStartTime = Math.floor(Number(endOfDay(this.startTime * 1000)) / 1000);
                const days = durationToDays(adjustedStartTime - nowUnixTimestamp());

                if (days === 1 && !this.labels.day) return '';
                if (days > 1 && !this.labels.days) return '';

                return `${days} ${
                    days === 1 ? this.labels.day : this.labels.days
                }`;
            } else {
                return durationToHMS(this.timeLeft);
            }
        }
    },

    watch: {
        state: {
            immediate: true,
            handler(newVal): void {
                console.debug('[Countdown] state changed to ' + newVal);
                this.api.customAction({ newState: newVal }, 'countdown');
                this.resolveMutingState();
                if (newVal === this.STATES.STREAM_IS_LIVE) {
                    this.unmute();
                }
                this.api.disableNotifications(newVal !== this.STATES.STREAM_IS_LIVE);
            }
        },

        startTime() {
            this.updateCurrentTime();
        }
    },

    created(): void {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.onNewState(partial);
            }
        );

        this.onNewState(state);

        this.unsubscribers.push(
            unsubscriber,
            this.api.onMetadataChange((payload) => this.onMetadataChange(payload))
        );

        this.updateCurrentTime();
        this.resolveMutingState();
    },

    beforeDestroy(): void {
        clearInterval(this.countdownInterval);
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }
        this.api.disableNotifications(false);
    },

    methods: {
        onNewState(partial: Partial<PlayerState>): void {
            this.muted = get(partial, 'muted', this.muted);

            const keys = ['isPlaying', 'videoLoaded', 'muted'];
            const shouldResolveMutingState = Object.keys(partial).some(key => keys.includes(key));
            if (shouldResolveMutingState) {
                this.resolveMutingState();
            }
        },

        resolveMutingState() {
            if (!this.muted && this.state !== this.STATES.STREAM_IS_LIVE) {
                this.api.mute();
            }
        },

        updateCurrentTime(): void {
            const currentTime = nowUnixTimestamp();
            this.timeLeft = this.startTime - currentTime;

            if (this.timeLeft <= 0) {
                this.timeLeft = 0;
                clearInterval(this.countdownInterval);
                this.countdownInterval = 0;
            } else if (!this.countdownInterval) {
                this.countdownInterval = setInterval(
                    () => this.updateCurrentTime(),
                    1000);
            }
        },

        onMetadataChange(payload: unknown): void {
            const streamIsLive = get(payload, 'value.is_live');

            const tag = cloneDeep(get(payload, 'value'));
            this.api.customAction({ tag }, 'id3');

            if (streamIsLive && !this.streamIsLive) {
                this.api.customAction({ streamIsLive: true }, 'countdown');
            }

            if (!streamIsLive && this.streamIsLive && !this.streamEnded) {
                this.api.customAction({ streamEnded: true }, 'countdown');
            }
        },

        async unmute() {
            // Here we have the risk that the user still hasn't interacted with the document.
            // In that case, if we simply call this.api.unmute(); the browser will detect an unauthorized
            // un-muting without user interaction, and will force pause.
            const canAutoPlayAudio = await canAutoPlay.video({ muted: false });
            if (canAutoPlayAudio.result) {
                console.debug('[Countdown] Unmuting', canAutoPlayAudio);
                this.api.unmute();
            }
        }
    }
});
